import dayjs, { Dayjs } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);
dayjs.extend(timezone);

export const DATE_TIME_FORMAT = "YYYY-MM-DDTHH:mm:ss" as const;
export const DATE_FORMAT = "YYYY-MM-DD" as const;
export const ASIA_SEOUL_TIMEZONE = "Asia/Seoul" as const;

export type DayjsUnitType =
  | "millisecond"
  | "second"
  | "minute"
  | "hour"
  | "day"
  | "week"
  | "month"
  | "quarter"
  | "year";

// 두 날짜 차이를 unit값에 따라 구하기.
export const getTimeDifference = (
  startTime = "",
  endTime = "",
  unit: DayjsUnitType
) => {
  if (!startTime || !endTime || !unit) {
    return "";
  }

  let diff = 0;
  const start = dayjs(startTime).tz(ASIA_SEOUL_TIMEZONE);
  const end = dayjs(endTime).tz(ASIA_SEOUL_TIMEZONE);
  if (unit === "hour") {
    const diffInMinutes = end.diff(start, "minute"); // 분 단위로 차이 계산.
    const diffInHours = diffInMinutes / 60; // 분을 시간으로 변환.
    diff = Number(diffInHours.toFixed(2)); // 소수 2짜리까지 반환
  } else {
    diff = end.diff(start, unit);
  }
  return diff;
};

// now의 시간이 HH:00 ~ HH:30인지, HH:30 ~ HH:00인지 start와 end리턴.
// now값은 timezone설정을 해서 넘기기.
export const getStartAndEndTime = (now: Dayjs) => {
  if (!now) {
    return { start: "", end: "" };
  }
  // 현재 시간의 분 부분을 가져와서 30분 단위로 나누기
  const minutes = now.minute();
  const startMinutes = Math.floor(minutes / 30) * 30;
  const endMinutes = startMinutes + 30;

  // 시작 시간과 종료 시간 계산
  const start = dayjs(
    now.set("minute", startMinutes).set("second", 0).set("millisecond", 0)
  )
    .tz(ASIA_SEOUL_TIMEZONE)
    .format(DATE_TIME_FORMAT);
  const end = dayjs(
    now.set("minute", endMinutes).set("second", 0).set("millisecond", 0)
  )
    .tz(ASIA_SEOUL_TIMEZONE)
    .format(DATE_TIME_FORMAT);

  return { start, end };
};

// 날짜 + 시간 형태의 string값을 날짜와 시간으로 분리.
export const splitDateTime = (dateTime = "") => {
  if (!dateTime) {
    return { date: "", time: "" };
  }
  const [date, time] = dateTime.split("T");
  return { date, time };
};

// 오전 오후 formatting
export const convertTimeToAMPM = (date = "") => {
  if (!date) {
    return "";
  }

  const kDate = dayjs(date).tz(ASIA_SEOUL_TIMEZONE);
  const formatString = kDate.format("A");
  const unit = formatString === "AM" ? "오전" : "오후";
  return `${unit} ${kDate.format("HH:mm")}`;
};

// 오늘 날짜를 YYYY-MM-DD포맷으로 가져오기
export const getTodayDate = () => {
  return dayjs().tz(ASIA_SEOUL_TIMEZONE).format(DATE_FORMAT);
};

// YYYY-MM-DD포맷으로 변환
export const convertDate = (date: string | Date | Dayjs) => {
  return dayjs(date).tz(ASIA_SEOUL_TIMEZONE).format(DATE_FORMAT);
};

// Date Object로 변환
export const convertDateObject = (date: string | Date) => {
  return new Date(date);
};

// 30분 단위의 시간을 생성하는 함수
export const generateTimeOptions = () => {
  const options = [];
  const date = new Date(0); // Initialize to 00:00
  for (let i = 0; i < 48; i++) {
    // 24 hours * 2 slots per hour
    const timeString = date.toTimeString().substring(0, 5);
    options.push(timeString);
    date.setMinutes(date.getMinutes() + 30);
  }
  return options;
};

// stdDate를 기준으로 전달 마지막주, 이번달 첫째주까지 날짜 리턴.
export const getDatesForPreviousMonthAndCurrentMonth = (cur: string) => {
  const current = dayjs(cur);

  // 전달 첫째 날 (일요일을 기준으로)
  const startDate = current
    .subtract(1, "month")
    .startOf("month")
    .format(DATE_FORMAT);

  // 다음 달 마지막 날 (토요일을 기준으로)
  const endDate = current.add(1, "month").endOf("month").format(DATE_FORMAT);

  return { startDate, endDate };
};

// 주어진 날짜가 오늘보다 이전날짜인지 검사.
export const isPastDate = (stdDate: string) => {
  const formatted = dayjs(stdDate);
  const today = dayjs();
  return formatted.isBefore(today, "day");
};

export const isValidDateFormat = (stdDate?: string) => {
  return dayjs(stdDate).isValid();
};

export const convertDateToKRDate = (stdDate: string) => {
  return dayjs(stdDate).format("YYYY년 MM월 DD일");
};

export const utcToLocalDate = (utcTime: string, timeZone?: string): string => {
  const resolvedTimeZone = timeZone ?? dayjs.tz.guess(); // 브라우저의 타임존
  return dayjs.utc(utcTime).tz(resolvedTimeZone).format("YYYY-MM-DDTHH:mm:ss");
};

export const formatToLocalDateTime = (
  localTime: string,
  timeZone?: string
): string => {
  const resolvedTimeZone = timeZone ?? dayjs.tz.guess(); // 브라우저의 타임존
  return dayjs(localTime).tz(resolvedTimeZone).format("YYYY-MM-DDTHH:mm:ss");
};

export const isToday = (date?: Date) => {
  if (!date) return false;
  return dayjs(date).isSame(dayjs(), "day");
};
